import React from 'react'
import type { Step } from 'react-joyride'
import type { TourStepKey } from '~/features/tour/types'

export const stepDefinitions: Record<TourStepKey, Step> = {
  welcome: {
    target: 'body',
    placement: 'center',
    content: (
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-content">Welcome to RushDB!</h2>
        <p className="text-content2">
          Congratulations on creating your account. We've already created your first project and API key —
          let's walk through the key features.
        </p>
      </div>
    ),
    data: {
      route: 'home',
      redirectTo: 'projectHelp',
      key: 'welcome'
    }
  },
  projectSdkTokenOverview: {
    target: '[data-tour="project-help-sdk-input"]',
    placement: 'top',
    content: (
      <div className="space-y-4">
        <h3 className="text-lg font-bold text-content">Your First SDK Key</h3>
        <p className="text-content2">
          We’ve generated an API key so you can immediately start using our
          <a
            className="ml-1 text-accent underline"
            href="https://docs.rushdb.com/get-started/quick-tutorial/"
            target="_blank"
            rel="noreferrer"
          >
            SDKs
          </a>
          .
        </p>
      </div>
    ),
    data: {
      route: 'projectHelp',
      key: 'projectSdkTokenOverview',
      noBack: true
    }
  },
  projectSdkTokenTabInfo: {
    target: '[data-tour="project-token-chip"]',
    placement: 'bottom',
    content: (
      <div className="space-y-4">
        <h3 className="text-lg font-bold text-content">API Keys Tab</h3>
        <p className="text-content2">Create or revoke API tokens to control secure access to your project.</p>
      </div>
    ),
    data: {
      route: 'projectHelp',
      key: 'projectSdkTokenTabInfo'
    }
  },
  projectImportDataTab: {
    target: '[data-tour="project-import-data-chip"]',
    placement: 'bottom',
    content: (
      <div className="space-y-4">
        <h3 className="text-lg font-bold text-content">Import Data Tab</h3>
        <p className="text-content2">
          Upload your JSON or NDJSON — RushDB will handle the rest. We’ll guide you through it shortly.
        </p>
      </div>
    ),
    data: {
      route: 'projectHelp',
      key: 'projectImportDataTab',
      redirectTo: 'projectImportData'
    }
  },
  projectImportRadio: {
    target: '[data-tour="project-import-data-radio"]',
    placement: 'bottom',
    content: (
      <div className="space-y-4">
        <h3 className="text-lg font-bold text-content">Push Any JSON</h3>
        <p className="text-content2">
          RushDB turns raw JSON into queryable graph records and relationships without requiring a schema
          upfront.
        </p>
      </div>
    ),
    data: {
      route: 'projectImportData',
      key: 'projectImportRadio',
      nextShouldBeManuallySet: true,
      noNext: true
    }
  },
  projectImportOverview: {
    target: '[data-tour="project-import-data-overview"]',
    placement: 'bottom',
    content: (
      <div className="space-y-4">
        <h3 className="text-lg font-bold text-content">Preview Your Data</h3>
        <p className="text-content2">
          In this example, the JSON contains agents, evaluation runs, conversations, and tool calls. Import it
          to see how nested data becomes a graph.
        </p>
      </div>
    ),
    data: {
      route: 'projectImportData',
      key: 'projectImportOverview',
      noBack: true
    }
  },
  projectImportIngest: {
    target: '[data-tour="project-import-data-ingest"]',
    placement: 'top',
    content: (
      <div className="space-y-4">
        <h3 className="text-lg font-bold text-content">Import Your Data</h3>
        <p className="text-content2">
          Upload your JSON — RushDB automatically transforms it into graph records and relationships. Once
          complete, your data appears instantly, ready to query. Press Import JSON to continue.
        </p>
      </div>
    ),
    data: {
      route: 'projectImportData',
      key: 'projectImportIngest',
      nextShouldBeManuallySet: true,
      noNext: true,
      waitForManualAction: true
    }
  },
  projectIndexSuggestions: {
    target: '[data-tour="project-index-suggestions"]',
    placement: 'top',
    content: (
      <div className="space-y-4">
        <h3 className="text-lg font-bold text-content">Suggested Semantic Indexes</h3>
        <p className="text-content2">
          RushDB inspected your imported schema and found text fields that are useful for semantic search.
        </p>
      </div>
    ),
    data: {
      route: 'projectIndexes',
      key: 'projectIndexSuggestions',
      noBack: true
    }
  },
  projectIndexCreate: {
    target: '[data-tour="project-index-suggested-create"]',
    placement: 'left',
    content: (
      <div className="space-y-4">
        <h3 className="text-lg font-bold text-content">Create an Index</h3>
        <p className="text-content2">
          Index a long-text field to enable semantic search — finding records by meaning, not just exact
          keywords.
        </p>
      </div>
    ),
    data: {
      route: 'projectIndexes',
      key: 'projectIndexCreate',
      nextShouldBeManuallySet: true,
      noNext: true,
      waitForManualAction: true
    }
  },
  projectRelationshipAnalyze: {
    target: '[data-tour="project-relationships-analyze"]',
    placement: 'left',
    content: (
      <div className="space-y-4">
        <h3 className="text-lg font-bold text-content">Find Relationship Patterns</h3>
        <p className="text-content2">
          RushDB analyzes your data to find hidden connections — reference fields that link record types, and
          default relationships that can get meaningful names.
        </p>
      </div>
    ),
    data: {
      route: 'projectSuggestedRelationships',
      key: 'projectRelationshipAnalyze',
      nextShouldBeManuallySet: true,
      noNext: true
    }
  },
  projectRelationshipApprove: {
    target: '[data-tour="project-relationships-approve"]',
    placement: 'left',
    content: (
      <div className="space-y-4">
        <h3 className="text-lg font-bold text-content">Approve a Suggested Pattern</h3>
        <p className="text-content2">
          Approving a pattern materializes the inferred relationship now and keeps applying it to future
          writes.
        </p>
      </div>
    ),
    data: {
      route: 'projectSuggestedRelationships',
      key: 'projectRelationshipApprove',
      nextShouldBeManuallySet: true,
      noNext: true,
      waitForManualAction: true
    }
  },
  recordGraphView: {
    target: 'body',
    placement: 'center',
    content: (
      <div className="space-y-4">
        <h3 className="text-lg font-bold text-content">See the Graph</h3>
        <p className="text-content2">
          Your imported records and approved relationships are now visible together as a connected graph you
          can explore.
        </p>
      </div>
    ),
    data: {
      route: 'project',
      key: 'recordGraphView',
      noBack: true
    }
  },
  recordRawApiMode: {
    target: '[data-tour="project-query-lab-chip"]',
    placement: 'right',
    content: (
      <div className="space-y-4">
        <h3 className="text-lg font-bold text-content">Open the Query Lab</h3>
        <p className="text-content2">
          The Query Lab is a playground for the query API — write, tweak, and run queries directly against
          your data and see exactly what your apps would get back.
        </p>
      </div>
    ),
    data: {
      route: 'project',
      key: 'recordRawApiMode',
      nextShouldBeManuallySet: true,
      noNext: true,
      clickTarget: '[data-tour="project-query-lab-chip"]'
    }
  },
  rawApiSelectQuery: {
    target: '[data-tour="raw-api-payload"]',
    placement: 'right',
    content: (
      <div className="space-y-4">
        <h3 className="text-lg font-bold text-content">Try a Query</h3>
        <p className="text-content2">
          We prefilled an example that aggregates counts, sums, and averages across related records in a
          single call. Feel free to tweak it — this editor is yours to experiment in.
        </p>
      </div>
    ),
    data: {
      route: 'projectQueryLab',
      key: 'rawApiSelectQuery',
      noBack: true
    }
  },
  rawApiRunQuery: {
    target: '[data-tour="raw-api-run-query"]',
    placement: 'left',
    content: (
      <div className="space-y-4">
        <h3 className="text-lg font-bold text-content">Run the Query</h3>
        <p className="text-content2">
          Execute it to see how RushDB turns graph records into aggregated metrics on the fly.
        </p>
      </div>
    ),
    data: {
      route: 'projectQueryLab',
      key: 'rawApiRunQuery',
      nextShouldBeManuallySet: true,
      noNext: true,
      waitForManualAction: true
    }
  },
  rawApiResults: {
    target: '[data-tour="raw-api-result"]',
    placement: 'left',
    content: (
      <div className="space-y-4">
        <h3 className="text-lg font-bold text-content">Instant Aggregations</h3>
        <p className="text-content2">
          The result gives you immediate operational stats without defining a schema or building a pipeline.
        </p>
      </div>
    ),
    data: {
      route: 'projectQueryLab',
      key: 'rawApiResults',
      redirectTo: 'projectHelp'
    }
  },
  projectGettingStartedFinish: {
    target: '[data-tour="project-getting-started-finish"]',
    placement: 'bottom',
    content: (
      <div className="space-y-4">
        <h3 className="text-lg font-bold text-content">Connect Your Apps</h3>
        <p className="text-content2">
          You have imported data, indexed text, approved a relationship, visualized the graph, and queried
          metrics. Use Getting Started when you are ready to connect your tools, apps, and workflows.
        </p>
      </div>
    ),
    data: {
      route: 'projectHelp',
      key: 'projectGettingStartedFinish',
      noBack: true
    }
  }
}

export const steps: Step[] = Object.values(stepDefinitions)
export const keys = Object.keys(stepDefinitions) as TourStepKey[]
